import type { RefObject } from 'react'
import { useLayoutEffect } from 'react'

// Natively focusable elements plus anything with an explicit tabindex. Used to
// pull a whole slide out of the keyboard tab order when it scrolls out of the
// carousel viewport.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable="true"]',
  '[tabindex]',
].join(',')

// Remembers the author-provided tabindex so it can be restored once the slide
// becomes focusable again.
const PREV_TABINDEX_KEY = 'fwPrevTabindex'

const SCROLLABLE_OVERFLOW = /auto|scroll|overlay/

/**
 * Scroll containers are focusable in their own right: browsers put a scroller
 * with no keyboard-focusable descendants into the tab order so it can still be
 * scrolled from the keyboard (Chrome's "keyboard focusable scrollers"). Gating
 * the controls inside a scroller is therefore what *promotes* the scroller
 * itself into the tab order, so it has to be gated too.
 *
 * Keyed off the computed overflow rather than whether the element currently
 * overflows: a collapsed product list is a tab stop while measuring
 * `scrollWidth === clientWidth`.
 */
function isScrollContainer(el: HTMLElement): boolean {
  const style = getComputedStyle(el)
  // The shorthand is read as well as the longhands: engines disagree about
  // which of them a declared `overflow: auto` is reflected in.
  return (
    SCROLLABLE_OVERFLOW.test(style.overflowX) ||
    SCROLLABLE_OVERFLOW.test(style.overflowY) ||
    SCROLLABLE_OVERFLOW.test(style.overflow)
  )
}

/**
 * Keyboard focus containment for a carousel slide.
 *
 * While `isFocusable === false`, every focusable descendant of `ref` is pulled
 * out of the tab order, so keyboard users only ever land on the slides that are
 * actually visible in the carousel. Callers pair this with `aria-hidden` on the
 * same element so the two never disagree (axe `aria-hidden-focus`).
 *
 * This owns the tab order for the entire subtree rather than gating each
 * control individually, which is the point: hand-threading `tabIndex` only
 * covers the controls the author remembered, and silently misses lazily
 * rendered ones (CTA buttons, product cards, provider iframes), chrome added
 * later (a social attribution link outside the thumbnail), and elements that
 * are focusable without ever declaring it (scroll containers). For the same
 * reason a subtree must be owned by exactly one gate: React re-rendering a
 * child with an explicit `tabIndex` while gated would be captured as that
 * child's "author" value and written back on restore, leaving it untabbable.
 *
 * Only `tabindex` is toggled, never `inert`, so a partially visible slide can
 * still be clicked or tapped.
 */
export function useFocusableGate(
  ref: RefObject<HTMLElement | null>,
  isFocusable: boolean | undefined,
): void {
  useLayoutEffect(() => {
    const root = ref.current
    if (!root) {
      return
    }

    const gate = (el: HTMLElement) => {
      if (el.dataset[PREV_TABINDEX_KEY] === undefined) {
        el.dataset[PREV_TABINDEX_KEY] = el.getAttribute('tabindex') ?? ''
      }
      el.setAttribute('tabindex', '-1')
    }

    const restore = (el: HTMLElement) => {
      const prev = el.dataset[PREV_TABINDEX_KEY]
      if (prev === undefined) {
        return
      }
      if (prev === '') {
        el.removeAttribute('tabindex')
      } else {
        el.setAttribute('tabindex', prev)
      }
      delete el.dataset[PREV_TABINDEX_KEY]
    }

    const forEachFocusable = (fn: (el: HTMLElement) => void) =>
      root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach(fn)

    // Scroll containers declare nothing in the DOM, so they cannot be found by
    // selector. Only needed when gating: once gated they carry `tabindex`, so
    // the selector above picks them up again on the way back.
    const gateScrollContainers = () =>
      root.querySelectorAll<HTMLElement>('*').forEach((el) => {
        if (isScrollContainer(el)) {
          gate(el)
        }
      })

    if (isFocusable === false) {
      forEachFocusable(gate)
      gateScrollContainers()
      // Re-gate focusables that mount after this pass (lazy CTA/product cards,
      // autoplay media). Attribute writes above are not observed, so this can't
      // loop. Only needed while off-screen; visible slides stay interactive.
      const observer = new MutationObserver(() => {
        forEachFocusable(gate)
        gateScrollContainers()
      })
      observer.observe(root, { childList: true, subtree: true })
      return () => observer.disconnect()
    }

    forEachFocusable(restore)
  }, [ref, isFocusable])
}
